import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Package, Printer, ShoppingBag, UserPlus } from 'lucide-react';
import { getGuestOrderConfirmation, formatOrderNumber, type GuestOrderConfirmationResponse } from '../../../utils/firebase';
export function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [order, setOrder] = useState<GuestOrderConfirmationResponse | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [receiptError, setReceiptError] = useState('');
  const money = (cents: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);
  const loadReceipt = async () => {
    if (!sessionId) {
      setReceiptError('Missing checkout session.');
      return;
    }
    if (order) {
      setShowReceipt(true);
      return;
    }
    try {
      setLoadingReceipt(true);
      const data = await getGuestOrderConfirmation(sessionId);
      console.log(data);
      setOrder(data);
      setShowReceipt(true);
    } catch {
      setReceiptError('Unable to load your receipt. Please try again.');
    } finally {
      setLoadingReceipt(false);
    }
  };
  return <div className="w-full min-h-screen flex flex-col items-center justify-center pt-32 pb-12 px-6 bg-[#FFFFFF]" data-id="element-2660" data-mp="2niz5908lpc2m">
      <div className="w-full max-w-[400px]" data-id="element-2661" data-mp="29j7leivel1gp">
        <div className="text-center mb-8" data-id="element-2662" data-mp="2dlukn4uelonx">
          <div className="flex justify-center mb-6" data-id="element-2663" data-mp="2dnnrak50o8vx">
            <CheckCircle2 className="w-12 h-12 text-tealslate" data-id="element-2664" data-mp="1zml23yiq6igs" />
          </div>
          <h1 className="text-4xl font-bold text-[#111111] mb-4 tracking-tight" data-id="element-2665" data-mp="1zonyr1edv9uh">
            Payment Successful
          </h1>
          <p className="text-sm font-medium text-[#333333] mb-2" data-id="element-2666" data-mp="3tzuhg6xycy6y">
            A receipt has been emailed to the address you used during checkout.
          </p>
          <p className="text-sm font-medium text-[#333333]" data-id="element-2667" data-mp="2ryn0oid6kjc">
            You'll receive another email when your order ships.
          </p>
        </div>

        <div className="space-y-4 mb-8" data-id="element-2668" data-mp="3si5ix6swg585">
          <Link to="/signup" className="w-full px-6 py-4 bg-tealslate text-white font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm flex items-center justify-center gap-2 text-[18px]" data-id="element-2669" data-mp="3sl9w2tocebay">
            Create Account
          </Link>

          <button onClick={loadReceipt} className="w-full px-6 py-4 bg-white border border-[#E5E5E5] text-[#111111] font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm flex items-center justify-center gap-2 text-[18px]" data-id="element-2670" data-mp="k2u8shey2ocq">
            {loadingReceipt ? 'Loading Receipt...' : 'View My Receipt'}
          </button>

          <Link to="/shop" className="w-full px-6 py-4 bg-white border border-[#E5E5E5] text-[#111111] font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm flex items-center justify-center gap-2 text-[18px]" data-id="element-2671" data-mp="12umzu6agdzht">
            Continue Shopping
          </Link>
        </div>

        {receiptError && <div className="text-center text-burgundy text-sm font-bold mb-6" data-id="element-2672" data-mp="1fc7j2a9988p3">
            {receiptError}
          </div>}

        {showReceipt && order && <div className="bg-white border border-[#E5E5E5] rounded-md p-6 shadow-sm text-left" data-id="element-2673" data-mp="1pamznjannzkc">
            <h2 className="font-bold text-[#111111] mb-4" data-id="element-2674" data-mp="1p8u0ep2dghmd">Items</h2>

            {order.lineItems.map(item => <div key={item.name} className="flex justify-between py-3 border-b border-[#E5E5E5] last:border-b-0 text-sm" data-id="element-2675" data-mp="1y38hmx6gq284">
                <div className="text-[#111111] font-medium" data-id="element-2676" data-mp="234607avdwya5">
                  {item.quantity} × {item.name}
                </div>
                <div className="text-[#111111] font-bold" data-id="element-2677" data-mp="2czpr1iv3d6s1">
                  {money(item.unitPrice * item.quantity)}
                </div>
              </div>)}

            <div className="mt-6 space-y-3 text-sm" data-id="element-2678" data-mp="39p9rbadjxx3m">
              <div className="flex justify-between" data-id="element-2679" data-mp="34rhwa7hm1uv7">
                <span className="text-[#5A5A5A]" data-id="element-2680" data-mp="3jhfr36a1pitd">Subtotal</span>
                <span className="text-[#111111] font-medium" data-id="element-2681" data-mp="3jgw61gp3meb4">{money(order.subtotal)}</span>
              </div>
              <div className="flex justify-between" data-id="element-2682" data-mp="3ekfm6yoxmm3u">
                <span className="text-[#5A5A5A]" data-id="element-2683" data-mp="2qryt7t3np9pr">Shipping</span>
                <span className="text-[#111111] font-medium" data-id="element-2684" data-mp="2ltnhfctswp4w">{money(order.shipping)}</span>
              </div>
              <div className="flex justify-between" data-id="element-2685" data-mp="2zt6pk5mm40h7">
                <span className="text-[#5A5A5A]" data-id="element-2686" data-mp="2zuzwzn51uoq9">Tax</span>
                <span className="text-[#111111] font-medium" data-id="element-2687" data-mp="2vrbon28lfp3y">{money(order.tax)}</span>
              </div>
              <div className="flex justify-between" data-id="element-2688" data-mp="f2g0fqcpvw7u">
                <span className="text-[#5A5A5A]" data-id="element-2689" data-mp="f5aixcc676ym">Discount</span>
                <span className="text-[#111111] font-medium" data-id="element-2690" data-mp="b0ue7eeam5kz">
                  -{money(order.discount)}
                </span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t border-[#E5E5E5] pt-4 mt-2" data-id="element-2691" data-mp="nk8776j07f4h">
                <span className="text-[#111111]" data-id="element-2692" data-mp="8mz3ddcikivv">Total</span>
                <span className="text-[#111111]" data-id="element-2693" data-mp="8os82n3kgt18">{money(order.total)}</span>
              </div>
            </div>

            <div className="border-t border-[#E5E5E5] mt-6 pt-6 text-sm" data-id="element-2694" data-mp="do4vb8yco24c">
              <h2 className="font-bold text-[#111111] mb-3" data-id="element-2695" data-mp="11c4w71pdl8wn">Shipping Address</h2>
              <p className="text-[#5A5A5A] leading-relaxed" data-id="element-2696" data-mp="169o7ptuov7kw">
                {order.shippingAddress.recipientName}<br data-id="element-2697" data-mp="rks4qb7hokr5" />
                {order.shippingAddress.line1}<br data-id="element-2698" data-mp="rjz3fpraunge" />
                {order.shippingAddress.line2 && <>{order.shippingAddress.line2}<br data-id="element-2699" data-mp="witlgphg07zg" /></>}
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}<br data-id="element-2700" data-mp="1p179xygw2hlv" />
                {order.shippingAddress.country}
              </p>
            </div>
          </div>}
      </div>
    </div>;
}